import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/** The gallery marks every slide with the media type it holds. */
const VIDEO_SELECTOR = '.product-media-container--video, .product-media-container--external_video';

/** Below this share of the video being on screen already, the page scrolls to it. */
const VISIBILITY_THRESHOLD = 0.6;

/**
 * "What fits inside" row: its last cell sends the shopper to the product's first video instead of
 * repeating a still. Pressing it selects that slide in the gallery, brings it on screen if it is not
 * already there, and starts playback — the interior of the bag is the answer to "what fits", so the
 * shortcut saves hunting for the right thumbnail.
 *
 * Markup contract (see `blocks/fits-row.liquid`):
 *   <fits-row>… <button on:click="/playVideo">…</button> …</fits-row>
 *
 * @extends Component
 */
class FitsRow extends Component {
  async playVideo() {
    const gallery = this.#gallery;
    const container = gallery?.querySelector(VIDEO_SELECTOR);

    if (!container) return;

    // Carousel galleries need the slide selected first; grid galleries show every media at once.
    const slide = container.closest('slideshow-slide');
    const slideshow = gallery.slideshow;
    const slides = slideshow?.slides;

    if (slide && slides) {
      const index = slides.indexOf(slide);
      if (index >= 0) await slideshow.select(index);
    }

    this.#bringOnScreen(container);
    this.#play(container.querySelector('deferred-media'));
  }

  /** The main gallery of this product, preferring the one in the same section. */
  get #gallery() {
    return (
      this.closest('.shopify-section')?.querySelector('media-gallery') ??
      document.querySelector('media-gallery')
    );
  }

  /**
   * Scrolls to the video only when it is mostly off screen, so pressing the shortcut on a wide
   * screen (where the gallery sits next to the buy column) does not jump the page for no reason.
   * @param {Element} element
   */
  #bringOnScreen(element) {
    const rect = element.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const visible = Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0);

    if (rect.height > 0 && visible / rect.height >= VISIBILITY_THRESHOLD) return;

    element.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'center' });
  }

  /**
   * Plays a deferred media element: the first press swaps the poster for the real video, later ones
   * just start it again (`loadContent` returns early once the media is in the DOM).
   * @param {Element | null} media
   */
  async #play(media) {
    if (!media) return;

    await customElements.whenDefined('deferred-media');

    if (media.hasAttribute('data-media-loaded')) {
      if (typeof media.playMedia === 'function') media.playMedia();
      return;
    }

    if (typeof media.showDeferredMedia === 'function') {
      media.showDeferredMedia();
      return;
    }

    // Last resort while the element upgrades: its poster button carries the same action.
    media.querySelector('.deferred-media__poster-button')?.click();
  }
}

if (!customElements.get('fits-row')) {
  customElements.define('fits-row', FitsRow);
}
